import { useCallback, useEffect, useState } from 'react';
import { T } from '@design/index';
import { patientApi, type MyProviderReview, type ProviderReviews as Reviews } from '../api';

/**
 * What patients wrote about a provider, and this patient's own review.
 *
 * Every provider page here has shown a star average with "written reviews are
 * coming soon" underneath it. They were not coming — `GET/POST/DELETE
 * /providers/:idOrSlug/reviews` has been serving them all along with nothing
 * calling it, so the average had no visible basis and no patient could add to
 * it.
 *
 * The "visited this provider" label is the server's own finding from the
 * appointment record, not a claim the reviewer makes and not one this app
 * adds. And when the average has no written reviews behind it, the page says
 * so rather than letting a number stand as though it were opinion.
 */

const card: React.CSSProperties = {
  background: T.white, border: `1px solid ${T.border}`, borderRadius: 14,
  padding: 14, fontFamily: T.font,
};

function Stars({ value, size = 13 }: { value: number; size?: number }) {
  return (
    <span style={{ color: '#F59E0B', fontSize: size, letterSpacing: 1 }}>
      {'★'.repeat(Math.round(value))}
      <span style={{ color: T.border }}>{'★'.repeat(Math.max(0, 5 - Math.round(value)))}</span>
    </span>
  );
}

function fmtDate(v: string): string {
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function ProviderReviews({ providerKey }: { providerKey: string }) {
  const [data, setData] = useState<Reviews | null>(null);
  const [mine, setMine] = useState<MyProviderReview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [rating, setRating] = useState(5);
  const [title, setTitle] = useState('');
  const [comment, setComment] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, own] = await Promise.all([
        patientApi.reviews.list(providerKey, 50),
        patientApi.reviews.mine(providerKey).catch(() => ({ review: null, canReview: true })),
      ]);
      setData(list);
      setMine(own?.review ?? null);
      if (own?.review) {
        setRating(own.review.rating);
        setTitle(own.review.title ?? '');
        setComment(own.review.comment ?? '');
      }
    } catch (e: any) {
      setError(e?.body?.message || 'Reviews could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [providerKey]);

  useEffect(() => { void load(); }, [load]);

  const submit = async () => {
    setBusy(true);
    setError('');
    try {
      const r = await patientApi.reviews.submit(providerKey, {
        rating,
        ...(title.trim() ? { title: title.trim() } : {}),
        ...(comment.trim() ? { comment: comment.trim() } : {}),
      });
      setData(r);
      setEditing(false);
      const own = await patientApi.reviews.mine(providerKey);
      setMine(own?.review ?? null);
    } catch (e: any) {
      setError(e?.body?.errors?.[0]?.message || e?.body?.message || 'That review could not be saved.');
    } finally {
      setBusy(false);
    }
  };

  const withdraw = async () => {
    setBusy(true);
    setError('');
    try {
      setData(await patientApi.reviews.remove(providerKey));
      setMine(null);
      setRating(5); setTitle(''); setComment('');
    } catch (e: any) {
      setError(e?.body?.message || 'That review could not be withdrawn.');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <div style={{ ...card, textAlign: 'center', fontSize: 12, color: T.textSec }}>Loading reviews…</div>;
  }

  if (!data) {
    return <div style={{ ...card, fontSize: 11.5, color: T.textSec }}>{error || 'Reviews are unavailable right now.'}</div>;
  }

  const written = data.reviews.length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ fontSize: 24, fontWeight: 800, color: T.text }}>
            {data.rating > 0 ? data.rating.toFixed(1) : '—'}
          </div>
          <div>
            <Stars value={data.rating} />
            <div style={{ fontSize: 10.5, color: T.textTer, marginTop: 2 }}>
              {/* An average with nothing written behind it is a different
                  claim from one built out of reviews, so it is labelled. */}
              {data.reviewCount > 0
                ? `${data.reviewCount} rating${data.reviewCount === 1 ? '' : 's'}${data.ratingIsFromReviews ? '' : ' · no written reviews yet'}`
                : 'Not rated yet'}
            </div>
          </div>
        </div>

        {written > 0 && (
          <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {data.breakdown.map((b) => (
              <div key={b.star} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 10, color: T.textTer, width: 12 }}>{b.star}</span>
                <div style={{ flex: 1, height: 5, borderRadius: 3, background: T.ice, overflow: 'hidden' }}>
                  <div style={{ width: `${written ? (b.count / written) * 100 : 0}%`, height: '100%', background: '#F59E0B' }} />
                </div>
                <span style={{ fontSize: 10, color: T.textTer, width: 16, textAlign: 'right' }}>{b.count}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {error && <div style={{ ...card, borderColor: `${T.danger}55`, fontSize: 11.5, color: T.danger }}>{error}</div>}

      {editing || !mine ? (
        <div style={card}>
          <div style={{ fontSize: 12.5, fontWeight: 800, color: T.text }}>
            {mine ? 'Edit your review' : 'Leave a review'}
          </div>
          <div style={{ fontSize: 10, color: T.textTer, marginTop: 3, lineHeight: 1.5 }}>
            It is published under your first name and an initial. If you have a visit on record here, it is marked
            as one — that is the server's finding, not something you tick.
          </div>

          <div style={{ display: 'flex', gap: 4, marginTop: 10 }}>
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                onClick={() => setRating(n)}
                aria-label={`${n} star${n === 1 ? '' : 's'}`}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                  fontSize: 22, color: n <= rating ? '#F59E0B' : T.border, lineHeight: 1,
                }}
              >
                ★
              </button>
            ))}
          </div>

          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={120}
            placeholder="A short headline (optional)"
            style={{
              width: '100%', marginTop: 10, padding: '9px 11px', borderRadius: 9,
              border: `1px solid ${T.border}`, fontSize: 12, fontFamily: T.font,
              color: T.text, outline: 'none', boxSizing: 'border-box',
            }}
          />
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            maxLength={2000}
            rows={3}
            placeholder="What was your experience? (optional)"
            style={{
              width: '100%', marginTop: 8, padding: '9px 11px', borderRadius: 9,
              border: `1px solid ${T.border}`, fontSize: 12, fontFamily: T.font,
              color: T.text, outline: 'none', boxSizing: 'border-box', resize: 'vertical',
            }}
          />

          <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
            <button
              onClick={submit}
              disabled={busy}
              style={{
                padding: '9px 16px', borderRadius: 9, border: 'none', cursor: busy ? 'default' : 'pointer',
                fontSize: 11.5, fontWeight: 700, color: T.white, background: T.teal,
                fontFamily: T.font, opacity: busy ? 0.6 : 1,
              }}
            >
              {busy ? 'Saving…' : (mine ? 'Update review' : 'Publish review')}
            </button>
            {editing && (
              <button
                onClick={() => setEditing(false)}
                style={{
                  padding: '9px 16px', borderRadius: 9, border: `1px solid ${T.border}`,
                  background: T.white, cursor: 'pointer', fontSize: 11.5, fontWeight: 700,
                  color: T.textSec, fontFamily: T.font,
                }}
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      ) : (
        <div style={{ ...card, borderColor: `${T.teal}55` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start' }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 11.5, fontWeight: 800, color: T.text }}>Your review</div>
              <div style={{ marginTop: 4 }}><Stars value={mine.rating} /></div>
              {mine.title && <div style={{ fontSize: 12, fontWeight: 700, color: T.text, marginTop: 6 }}>{mine.title}</div>}
              {mine.comment && <div style={{ fontSize: 11.5, color: T.textSec, marginTop: 4, lineHeight: 1.6 }}>{mine.comment}</div>}
              <div style={{ fontSize: 10, color: T.textTer, marginTop: 6 }}>
                {fmtDate(mine.at)}{mine.verifiedVisit ? ` · visit on record${mine.visitKind ? ` (${mine.visitKind.toLowerCase()})` : ''}` : ''}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button
              onClick={() => setEditing(true)}
              style={{
                padding: '7px 13px', borderRadius: 9, border: `1px solid ${T.border}`,
                background: T.white, cursor: 'pointer', fontSize: 11, fontWeight: 700,
                color: T.teal, fontFamily: T.font,
              }}
            >
              Edit
            </button>
            <button
              onClick={withdraw}
              disabled={busy}
              style={{
                padding: '7px 13px', borderRadius: 9, border: `1px solid ${T.danger}40`,
                background: T.white, cursor: busy ? 'default' : 'pointer', fontSize: 11,
                fontWeight: 700, color: T.danger, fontFamily: T.font,
              }}
            >
              {busy ? 'Withdrawing…' : 'Withdraw'}
            </button>
          </div>
        </div>
      )}

      {/* The list includes this patient's own review; it already has its own
          card above, with the controls. */}
      {data.reviews
        .filter((r) => r.id !== mine?.id)
        .map((r) => (
          <div key={r.id} style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start' }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 11.5, fontWeight: 800, color: T.text }}>
                  {r.author}
                  {r.verifiedVisit && (
                    <span style={{
                      marginLeft: 6, fontSize: 8.5, fontWeight: 700, color: T.white,
                      background: T.teal, borderRadius: 5, padding: '2px 6px', verticalAlign: 'middle',
                    }}>
                      Visited
                    </span>
                  )}
                </div>
                <div style={{ marginTop: 4 }}><Stars value={r.rating} size={11} /></div>
                {r.title && <div style={{ fontSize: 12, fontWeight: 700, color: T.text, marginTop: 6 }}>{r.title}</div>}
                {r.comment && <div style={{ fontSize: 11.5, color: T.textSec, marginTop: 4, lineHeight: 1.6 }}>{r.comment}</div>}
              </div>
              <div style={{ fontSize: 9.5, color: T.textTer, flexShrink: 0 }}>{fmtDate(r.at)}</div>
            </div>
          </div>
        ))}

      {written === 0 && (
        <div style={{ ...card, textAlign: 'center', fontSize: 11.5, color: T.textSec, lineHeight: 1.6 }}>
          Nobody has written about this provider yet. Yours would be the first.
        </div>
      )}
    </div>
  );
}
